import { PageHeader } from '@components/page-header';
import { Button, Grid, makeStyles, Theme, Typography } from '@material-ui/core';
import { Redirect, RouteComponentProps } from '@reach/router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import insightIcon from 'src/assets/icons/insights.svg';
import CarDetailDialog from 'src/components/CarDetailDialog';
import PageContainer from 'src/components/PageContainer';
import RecommendationCard from 'src/components/RecommendationCard';
import { RecommendationCardSkeleton, RecommendationRowSkeleton } from 'src/components/RecommendationCardSkeleton';
import { RoutePaths } from 'src/constants';
import { useFeatureFlags } from 'src/hooks/useFeatureFlags';
import { useRecommendations } from 'src/hooks/useRecommendations';
import { UserPermissions, useUserPermissions } from 'src/hooks/useUserPermissions';
import { CarRecommendationResponse, ListCarDTO, RecommendedAction, SourceType } from 'src/modules/generated/api';

const DUMMY_CARDS: CarRecommendationResponse[] = [
  {
    model: 'BMW 420d Gran Coupe Gran Coupe 2.0',
    imageThumbnailLink:
      'https://gis-media-assets-p.azureedge.net/images/WBA21AX000FP68993/assessments/ccb2784131ca65e5dfeddee89509daa59f1b541fc91f645f2ac57f5c779c4f9f/image-000.jpg?v=4327dff702dd',
    basePrice: { amount: 28500, currency: 'EUR' as const },
    recommendedAction: RecommendedAction.Buy,
    source: SourceType.Vwfs,
    carId: '2026061583',
    reason: { totalVehicleCount: 8, soldVehicleCount: 5, soldAvgTurnoverDays: 9.4 },
  },
  {
    model: 'Audi A6 Avant TDI quattro 150 kW S tronic',
    imageThumbnailLink: undefined,
    basePrice: { amount: 24900, currency: 'EUR' as const },
    recommendedAction: RecommendedAction.LowSupply,
    source: SourceType.AudiUcp,
    carId: '1IXLciDCH7nH6z8KnDUAKDWdCKV_OljfRsFBzaVPRzg=',
    reason: { totalVehicleCount: 3, soldVehicleCount: 1, soldAvgTurnoverDays: 17.5 },
  },
  {
    model: 'Volkswagen Golf 1.5 TSI',
    imageThumbnailLink:
      'https://gis-media-assets-p.azureedge.net/images/WVWZZZCD2PW534303/main/b4c0bec5ef4b1b1442a0e3a7623640d6a9cb98a2330d95fee1a6165265594fa3/image-000.jpg?v=d65c52aac03e',
    basePrice: undefined,
    recommendedAction: undefined,
    source: SourceType.Vwfs,
    carId: '2026314005',
    reason: undefined,
  },
  {
    model: 'Volvo XC60 Plus Dark 2WD 2.0 ',
    imageThumbnailLink:
      'https://gis-media-assets-p.azureedge.net/images/YV1UZK5VXP1301218/assessments/2710d9243723d25b9bdd9a74ddfb84de9769403dfaf685ca3d60ed0ed6b29275/image-000.jpg?v=c43a9cb034b7',
    basePrice: { amount: 24000, currency: 'EUR' as const },
    recommendedAction: RecommendedAction.LowSupply,
    source: SourceType.Vwfs,
    carId: '2026181383',
    reason: { totalVehicleCount: 4, soldVehicleCount: 2, soldAvgTurnoverDays: 12 },
  },
];

const useStyles = makeStyles((theme: Theme) => ({
  pageContainer: {
    maxWidth: 1500,
    paddingBlockEnd: theme.spacing(6),
  },
  pageHeader: {
    marginBlockEnd: theme.spacing(4.5),
  },
  pageHeadline: {
    display: 'flex',
    alignItems: 'end',
    gap: theme.spacing(1.5),
  },
  pageHeadlineIcon: {
    width: 40,
    height: 40,
    flexShrink: 0,
  },
  section: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  sectionHeading: {
    fontSize: '1.8rem',
    fontWeight: 'bold',
    textDecorationLine: 'none',
    borderLeft: `4px solid ${theme.palette.intelligence.main}`,
    paddingLeft: theme.spacing(1.5),
    marginBottom: theme.spacing(3),
  },
  subSectionTitle: {
    marginBottom: theme.spacing(2),
  },
  demoButton: {
    display: 'flex',
    width: 'fit-content',
    marginInlineStart: 'auto',
    marginBlockStart: theme.spacing(2),
  },
  subSectionTitleSecond: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
}));

const SKELETON_COUNT = 3;

const InsightPage = (_props: RouteComponentProps): JSX.Element => {
  const { t } = useTranslation();
  const classes = useStyles();
  const isEnabled = useFeatureFlags('irisRecommendation');
  const hasPermission = useUserPermissions(UserPermissions.IRIS_RECOMMENDATION);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [showDemoData, setShowDemoData] = useState(false);
  const { data, isLoading, isError } = useRecommendations();

  if (!isEnabled || !hasPermission) {
    return <Redirect to={RoutePaths.forbidden} noThrow />;
  }

  const dealerCars = data?.dealer ?? [];
  const regionCars = data?.region ?? [];
  const hasRealDealerData = dealerCars.length > 0;
  const hasRealRegionData = regionCars.length > 0;
  const displayDealerCars: CarRecommendationResponse[] =
    hasRealDealerData || showDemoData ? (hasRealDealerData ? dealerCars : DUMMY_CARDS) : [];
  const displayRegionCars: CarRecommendationResponse[] =
    hasRealRegionData || showDemoData ? (hasRealRegionData ? regionCars : DUMMY_CARDS) : [];

  const navigableCars = [...displayDealerCars, ...displayRegionCars].filter(
    (card) => card.source && card.carId,
  ) as ListCarDTO[];
  const selectedCar = selectedIndex !== null ? (navigableCars[selectedIndex] ?? null) : null;

  const rowItems = isLoading
    ? Array.from({ length: SKELETON_COUNT }, (_, i) => (
        <Grid item xs={12} md={6} key={`row-skeleton-${i}`}>
          <RecommendationRowSkeleton />
        </Grid>
      ))
    : displayDealerCars.map((card) => (
        <Grid item xs={12} md={6} key={`${card.source}-${card.carId}`}>
          <RecommendationCard
            {...card}
            variant="row"
            onOpen={
              card.source && card.carId
                ? () =>
                    setSelectedIndex(navigableCars.findIndex((c) => c.carId === card.carId && c.source === card.source))
                : undefined
            }
          />
        </Grid>
      ));

  const cardItems = isLoading
    ? Array.from({ length: SKELETON_COUNT }, (_, i) => (
        <Grid item xs={12} sm={6} md={3} key={`card-skeleton-${i}`} style={{ display: 'flex' }}>
          <RecommendationCardSkeleton />
        </Grid>
      ))
    : displayRegionCars.map((card) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={`${card.source}-${card.carId}`} style={{ display: 'flex' }}>
          <RecommendationCard
            {...card}
            variant="card"
            onOpen={
              card.source && card.carId
                ? () =>
                    setSelectedIndex(navigableCars.findIndex((c) => c.carId === card.carId && c.source === card.source))
                : undefined
            }
          />
        </Grid>
      ));

  return (
    <PageContainer className={classes.pageContainer} maxWidth={false}>
      <PageHeader
        className={classes.pageHeader}
        headline={
          <span className={classes.pageHeadline}>
            {t('insightPage.title')}
            <img src={insightIcon} alt="" className={classes.pageHeadlineIcon} />
          </span>
        }
        body={t('insightPage.subheader')}
      />
      <section className={classes.section}>
        <Typography className={classes.sectionHeading}>{t('insightPage.sectionRecommendations')}</Typography>
        {isError ? (
          <Typography>{t('recommendationsPage.error')}</Typography>
        ) : (
          <>
            <Typography variant="h3" className={classes.subSectionTitle}>
              {t('recommendationsPage.sectionDealer')}
            </Typography>
            <Grid container spacing={1}>
              {rowItems}
            </Grid>
            {!isLoading && !hasRealDealerData && !showDemoData && (
              <Typography>{t('recommendationsPage.empty')}</Typography>
            )}
            <Typography variant="h3" className={classes.subSectionTitleSecond}>
              {t('recommendationsPage.sectionRegion')}
            </Typography>
            <Grid container spacing={2} alignItems="stretch">
              {cardItems}
            </Grid>
            {!isLoading && !hasRealRegionData && !showDemoData && (
              <Typography>{t('recommendationsPage.empty')}</Typography>
            )}
          </>
        )}
      </section>
      {!isError && !isLoading && (!hasRealDealerData || !hasRealRegionData) && (
        <Button variant="outlined" className={classes.demoButton} onClick={() => setShowDemoData((prev) => !prev)}>
          {showDemoData ? 'Hide demo data' : 'Show demo data'}
        </Button>
      )}
      <CarDetailDialog
        car={selectedCar ?? undefined}
        index={selectedIndex ?? undefined}
        maxCars={navigableCars.length}
        moveToNext={() => setSelectedIndex((i) => (i !== null ? Math.min(i + 1, navigableCars.length - 1) : null))}
        moveToPrev={() => setSelectedIndex((i) => (i !== null ? Math.max(i - 1, 0) : null))}
        onClose={() => setSelectedIndex(null)}
      />
    </PageContainer>
  );
};

export default InsightPage;
